<template>
  <div class="settings-content__wrapper">
    <div class="horizontal layout center">
      <aha-icon name="stopwatch" class="settings-icon mb-0.5"></aha-icon>
      <span class="px-1 font-normal">{{ $i18nPresenter.t('Spin time') }}</span>
      <span class="title-table-presentation-tooltip result-layout-tooltip">
        <b-button
          v-tooltip="spinningDurationTooltip"
          class="tooltip-btn"
        />
        <i class="fas fa-question-circle" />
      </span>
    </div>
    <div class="time-toggle-wrapper">
      <div
        class="relative time-limit-content"
      >
        <aha-input
          v-model="spinningDuration"
          type="number"
          :error="errorTimeConfigVisible"
          @input="updateSpinningDuration"
        />
        <div class="unit-text">
          {{ $i18nPresenter.t('seconds') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState, mapActions } from 'vuex'
import SlideApi from '@/api/slide'
import Util from '@/util'
import Socket from '@/socket'
import eventCategories from '@/constant/eventCategories'
import WHEEL from '@/constant/SpinnerWheelConfig'

export default {
  data() {
    return {
      updateTimeConfigDebounce: null,
      spinningDuration: WHEEL.DEFAULT_SPINNING_DURATION,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    metadata() {
      return (this.slideActive && this.slideActive.metadata) || {}
    },
    spinningDurationTooltip() {
      return this.$i18nPresenter.t('Control the speed of the wheel')
    },
    errorTimeConfigVisible() {
      return !this.spinningDuration
        || this.spinningDuration < WHEEL.MIN_SPINNING_DURATION
        || this.spinningDuration > WHEEL.MAX_SPINNING_DURATION
    },
  },
  mounted() {
    this.spinningDuration = (this.metadata && this.metadata.spinningDuration)
      || WHEEL.DEFAULT_SPINNING_DURATION
  },
  methods: {
    ...mapActions(['setSyncLoadingAction', 'updateSlideActiveAction']),
    async updateSpinningDuration() {
      try {
        if (!this.spinningDuration
          || this.spinningDuration < WHEEL.MIN_SPINNING_DURATION
        ) return
        let spinningDurationNum = parseInt(this.spinningDuration, 10)

        if (spinningDurationNum > WHEEL.MAX_SPINNING_DURATION) {
          this.spinningDuration = WHEEL.MAX_SPINNING_DURATION
          spinningDurationNum = WHEEL.MAX_SPINNING_DURATION
        } else if (spinningDurationNum < WHEEL.MIN_SPINNING_DURATION) {
          this.spinningDuration = WHEEL.MIN_SPINNING_DURATION
          spinningDurationNum = WHEEL.MIN_SPINNING_DURATION
        }

        const payload = {
          metadata: {
            ...this.metadata,
            spinningDuration: spinningDurationNum,
          },
        }

        this.updateSlideActiveAction(payload)
        this.setSyncLoadingAction(1)

        await this.callUpdateSlideApi(payload)
        this.trackEditTime(spinningDurationNum)
        this.setSyncLoadingAction(2)
      } catch {
        this.setSyncLoadingAction(3)
      }
    },
    callUpdateSlideApi: debounce(async function debounceCallApi(payload) {
      await SlideApi.updateSlideById(this.slideActive.id, {
        accessCode: this.presentation.uniqueAccessCode,
        socketId: Socket.getSocketId(),
        ...payload,
      })
    }, 300),
    trackEditTime(time) {
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Right column > Edit setting > Spin time',
        `Presenter edit spin time: ${time}`,
        null,
        {
          presentationId: this.presentation.id,
          slideId: this.slideActive.id,
        },
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss-v2/variables.scss";
.settings-content__wrapper {
  @apply flex items-center justify-between;
  @apply py-4;
  &:hover {
    .title-table-presentation-tooltip {
      opacity: 1;
    }
    &.general-quiz-settings {
      @apply text-primary-100;
      .settings-icon,
      .settings-icon__arrow {
        @apply text-primary-100;
      }
    }
  }
  &.sub-content {
    margin-left: 1.75rem;
    border-top: none;
    padding-top: 0;
  }
  &.general-quiz-settings {
    @apply cursor-pointer;
    .settings-icon__arrow {
      @apply text-secondary-40;
    }
  }
  .settings-icon {
    color: $secondary-60-color;
  }
  .settings-content__link {
    color: inherit;
    text-decoration: underline;
  }
  .title-table-presentation-tooltip {
    opacity: 0;
    transition: all 0.2s ease-in-out;
  }
}
.time-limit-content {
  & ::v-deep .aha-input {
    width: 135px;
    padding-right: 70px;
  }
  .unit-text {
    @apply absolute top-2.5 right-2 text-base-70;
  }
}
</style>
